/**
 * Summary Builder
 *
 * @module workflows/diagnostics/materialize/summary-builder
 * @description Writes `summary.md` — the always-fits one-pager that's the agent's primary
 * entry point. Size-bounded by construction (counts and rollups, never per-event detail);
 * test asserts ≤80 lines for any input.
 */

import * as fs from 'node:fs/promises';
import { formatRelative, oneLine, truncate } from './_shared.js';
import { resolveWorkspacePath, stepRollupRelativePath, WORKSPACE_PATHS } from './paths.js';
import type {
  RunDataEvidenceSource,
  RunDataExecutionContext,
  RunDataSandboxLogs,
} from './run-data.js';

const MAX_NOTABLE_EVENTS = 5;
const MAX_HISTORY_LOOKBACK = 10;

// Upstream-divergence heuristic thresholds. Deliberately conservative — these surface
// *candidates to investigate*, not verdicts, so a false positive only costs the agent a
// glance. Tunable from here.
const MAX_DIVERGENCE_CANDIDATES = 5;
const MAX_INTENDED_INPUTS = 4;
/** Churn outlier (heuristic B): a non-failed step must clear BOTH to flag. */
const CHURN_MIN_THOUGHTS = 8;
const CHURN_MEDIAN_MULTIPLE = 1.5;
const CHURN_MIN_DOM_PROBES = 2;

export interface SummaryStepVariable {
  key: string;
  value: string | null;
  source: string | null;
  /** True when extracted from the live page (carries an extractor expression). */
  extracted: boolean;
}

export interface SummaryStep {
  id: string;
  orderIndex: number;
  name: string | null;
  status: string;
  startedAtMs: number | null;
  durationMs: number | null;
  cacheOutcome: string | null;
  cacheHitSource: string | null;
  errorMessage: string | null;
  /** Why a cache replay ended the way it did (e.g. 'replay_failed'). For divergence hints. */
  cacheOutcomeReason?: string | null;
  /** Cache replay failure bucket (e.g. 'element_absent'). For divergence hints. */
  cacheFailureCategory?: string | null;
  /** Variables this step set (configured inputs + extracted values). For divergence hints. */
  variablesSet?: ReadonlyArray<SummaryStepVariable>;
  /** Total agent thoughts attributed to this step. For the churn heuristic. */
  thoughtCount?: number;
  /** Thoughts that were direct DOM probes (run_code/evaluate). For the churn heuristic. */
  domProbeCount?: number;
}

export interface SummaryNetwork {
  timestampMs: number | null;
  url: string;
  method: string;
  status: number | null;
}

export interface SummaryConsole {
  timestampMs: number | null;
  type: string;
  text: string;
}

export interface SummaryNavigation {
  timestampMs: number | null;
  from: string | null;
  to: string | null;
  crossHost: boolean;
}

export interface SummaryRecentRun {
  testRunId: string;
  workflowRunId: string | null;
  status: string;
  /** ISO. */
  startedAt: string | null;
  /** ISO. */
  finishedAt: string | null;
  errorMessage?: string | null;
}

export interface SummaryLastGreenComparison {
  greenRunId: string;
  greenTestRunId: string;
  greenStartedAt: string | null;
  /** True/false when computed, null when the comparison could not be made. */
  authoredUnchanged: boolean | null;
}

export interface SummarySeedRun {
  seedWorkflowId: string | null;
  seedWorkflowName: string | null;
  seedRunId: string | null;
  status: string;
  errorMessage: string | null;
  failedStepName: string | null;
  failedStepError: string | null;
}

/**
 * Classification of the recent-history pattern leading into this failure. Gates the
 * regression steer: a single green→red is trustworthy evidence of an app change only in
 * `STABLE_REGRESSION`. `FLAKY` histories must NOT be read as app changes.
 */
export type HistoryRegime = 'STABLE_REGRESSION' | 'FLAKY' | 'NEW' | 'PERSISTENT_FAIL';

// Test-run status vocabulary: a clean pass is `success` (`passed` kept for legacy/fixtures);
// `flaky_success` passed only after a retry — it counts as a pass but is itself a flakiness
// signal. Failure-ish terminal states are `failed`/`timeout`/`degraded`; everything else
// (canceled, skipped, queued, waiting, running) carries no pass/fail signal.
const isCleanPassStatus = (s: string): boolean => s === 'passed' || s === 'success';
const isFlakyPassStatus = (s: string): boolean => s === 'flaky_success';
const isPassStatus = (s: string): boolean => isCleanPassStatus(s) || isFlakyPassStatus(s);
const isFailStatus = (s: string): boolean => s === 'failed' || s === 'timeout' || s === 'degraded';

/**
 * Classify the recent history (most-recent-first, current run excluded) into a regime.
 *
 * - `NEW` — fewer than 2 prior pass/fail runs; not enough signal.
 * - `PERSISTENT_FAIL` — no passes in the window; consistently failing.
 * - `STABLE_REGRESSION` — the window was clean-green (no fails, no flaky passes) or a solid
 *   clean-green streak (≥3 consecutive `success` immediately before the break). A pass→fail
 *   here is a real break.
 * - `FLAKY` — passes and fails interleave, or any `flaky_success` appears, without a solid
 *   recent clean-green streak; a single green→red is not trustworthy evidence of an app change.
 */
export const classifyHistoryRegime = (others: ReadonlyArray<{ status: string }>): HistoryRegime => {
  const seq = others.map((r) => r.status).filter((s) => isPassStatus(s) || isFailStatus(s));
  if (seq.length < 2) return 'NEW';
  const passes = seq.filter(isPassStatus).length;
  if (passes === 0) return 'PERSISTENT_FAIL';
  const fails = seq.filter(isFailStatus).length;
  const hasFlaky = seq.some(isFlakyPassStatus);
  // Clean-green streak immediately before the break (flaky passes do not extend it).
  let cleanGreenStreak = 0;
  for (const s of seq) {
    if (isCleanPassStatus(s)) cleanGreenStreak++;
    else break;
  }
  if ((fails === 0 && !hasFlaky) || cleanGreenStreak >= 3) return 'STABLE_REGRESSION';
  return 'FLAKY';
};

/**
 * How the workspace's consumer materializes a comparison run.
 *
 * - `'cli'` — a human (or CLI skill) reading a locally materialized workspace;
 *   the history hint prints the `canary workflow debug materialize` command.
 * - `'agent-tool'` — the in-process v2 diagnostic agent, whose sandboxed bash
 *   cannot run the CLI; the history hint points at its `materialize_run` tool.
 * - `'none'` — a comparison-leaf workspace (itself materialized as a baseline);
 *   the history hint reports stats only — no materialization instruction and no
 *   regime steer, since nobody investigates *from* this workspace.
 */
export type GreenRunAccess = 'agent-tool' | 'cli' | 'none';

export interface SummaryInput {
  workspaceRoot: string;
  runId: string;
  workflowName: string;
  runStatus: string;
  /** ISO. */
  runStartedAt: string | null;
  /** ISO. */
  runFinishedAt: string | null;
  reportedFailure: {
    failedStepId: string | null;
    failedStepName: string | null;
    failedStepOrderIndex: number | null;
    errorMessage: string | null;
  };
  steps: ReadonlyArray<SummaryStep>;
  network: ReadonlyArray<SummaryNetwork>;
  consoleLogs: ReadonlyArray<SummaryConsole>;
  navigation: ReadonlyArray<SummaryNavigation>;
  /**
   * Mid-run browser crashes (page crash / context closed / disconnect) that were
   * auto-recovered. Surfaced under "Notable events" so the diagnostic agent can
   * weigh whether a crash — not the test — caused the failure.
   */
  crashes?: ReadonlyArray<{ label: string; relativeTimestampMs: number }>;
  recentRuns: ReadonlyArray<SummaryRecentRun>;
  /** Seed flows this run provisioned; a seed failure is a common upstream cause. */
  seedRuns?: ReadonlyArray<SummarySeedRun>;
  /**
   * Variable keys (`$var.X`) the failing step's node config references. When one was
   * produced by an earlier extractor step, that producer becomes an upstream-divergence
   * candidate (the lineage heuristic). Empty when unknown.
   */
  failedStepReferencedVars?: ReadonlyArray<string>;
  /** Authored-definition comparison vs the most recent green run; null when none. */
  lastGreenComparison?: SummaryLastGreenComparison | null;
  /** How this workspace's consumer materializes comparison runs. Defaults to `'cli'`. */
  greenRunAccess?: GreenRunAccess;
  cliCommand?: string;
  execution?: RunDataExecutionContext;
  sandboxLogs?: RunDataSandboxLogs | null;
  /** Whether the retained gzip was successfully downloaded and decompressed locally. */
  sandboxLogsArtifactAvailable?: boolean;
  evidenceSources?: Record<string, RunDataEvidenceSource>;
}

export const writeSummary = async (input: SummaryInput): Promise<void> => {
  const baselineMs = input.runStartedAt ? Date.parse(input.runStartedAt) : null;
  const finishedMs = input.runFinishedAt ? Date.parse(input.runFinishedAt) : null;
  const durationMs = baselineMs !== null && finishedMs !== null ? finishedMs - baselineMs : null;

  const lines: string[] = [];

  // Header.
  lines.push(
    `# Run ${input.runId} — workflow "${input.workflowName}" — status: ${input.runStatus}`
  );
  const durationStr = durationMs !== null ? `${(durationMs / 1000).toFixed(3)}s` : 'unknown';
  lines.push(`runStartedAt: ${input.runStartedAt ?? 'unknown'}   duration: ${durationStr}`);
  lines.push('');

  const limitedSources = Object.entries(input.evidenceSources ?? {}).filter(
    ([, source]) => source.status !== 'available'
  );
  if (limitedSources.length > 0) {
    lines.push('## Evidence coverage');
    for (const [name, source] of limitedSources) {
      lines.push(
        `- ${name}: ${source.status}${source.reason ? ` — ${oneLine(source.reason)}` : ''}`
      );
    }
    lines.push('Treat conclusions that depend on these sources as lower confidence.');
    lines.push('');
  }

  if (input.execution) {
    lines.push('## Execution');
    const target = input.execution.targetEnvironment;
    const runtime = input.execution.runtimeEnvironment;
    lines.push(
      `Target: ${target ? `${target.name} (${target.type}, ${target.id})` : 'unknown'}; runtime: ${
        runtime ? `${runtime.name} (${runtime.type}, ${runtime.id})` : 'unknown'
      }`
    );
    const sandbox = input.execution.sandbox;
    if (sandbox) {
      lines.push(
        `Sandbox: ${sandbox.name} (${sandbox.id}) — ${sandbox.status}; logs: ${
          input.sandboxLogs
            ? input.sandboxLogs.status === 'available' || input.sandboxLogs.status === 'partial'
              ? input.sandboxLogsArtifactAvailable === false
                ? `${input.sandboxLogs.status}; retained artifact unavailable locally`
                : `${input.sandboxLogs.status} in ${WORKSPACE_PATHS.logsSandbox}`
              : `${input.sandboxLogs.status}${
                  input.sandboxLogs.reason ? ` (${input.sandboxLogs.reason})` : ''
                }`
            : 'not captured'
        }`
      );
      lines.push(
        'Sandbox logs are time-window scoped and may include concurrent activity from the same sandbox.'
      );
      if (input.sandboxLogs?.streamEventCounts) {
        const appStreams = Object.entries(input.sandboxLogs.streamEventCounts)
          .filter(([stream]) => stream.startsWith('sandbox/app/'))
          .sort(([left], [right]) => left.localeCompare(right));
        lines.push(
          `Application stdout/stderr: ${input.sandboxLogs.applicationEventCount ?? 0} events across ${appStreams.length} service stream${appStreams.length === 1 ? '' : 's'}.`
        );
        if (appStreams.length > 0) {
          lines.push(
            `Application streams: ${appStreams.map(([stream, count]) => `${stream} (${count})`).join(', ')}`
          );
        }
      }
    }
    lines.push('');
  }

  // Reported failure.
  if (input.runStatus === 'failed') {
    lines.push('## Reported failure');
    if (input.reportedFailure.failedStepName) {
      const ordinal =
        input.reportedFailure.failedStepOrderIndex !== null
          ? ` (step ${input.reportedFailure.failedStepOrderIndex})`
          : '';
      const err = input.reportedFailure.errorMessage
        ? ` — ${oneLine(input.reportedFailure.errorMessage)}`
        : '';
      lines.push(`Step${ordinal}: **${input.reportedFailure.failedStepName}**${err}`);
    } else if (input.reportedFailure.errorMessage) {
      lines.push(`Run-level error: ${oneLine(input.reportedFailure.errorMessage)}`);
    } else {
      lines.push('(no failed step identified — investigate run-level state)');
    }
    lines.push('**Hypothesis. Verify before trusting — root cause may be upstream.**');
    lines.push('');
  }

  // Candidate upstream divergences — heuristic hints (NOT verdicts) that point the
  // walk-backwards scan at non-failed steps worth confirming first. Omitted when empty.
  for (const l of buildDivergenceSection(input)) lines.push(l);

  // Seed flows — a seed runs as its own workflow run, so its failure won't appear
  // in the step table below. A failed seed is a frequent upstream root cause.
  const seedRuns = input.seedRuns ?? [];
  if (seedRuns.length > 0) {
    lines.push('## Seed flows');
    for (const seed of seedRuns.slice(0, MAX_NOTABLE_EVENTS)) {
      const label = seed.seedWorkflowName ?? seed.seedWorkflowId ?? 'seed flow';
      const glyph = STATUS_GLYPH[seed.status] ?? '?';
      const detail =
        seed.status === 'failed'
          ? seed.failedStepName
            ? ` — failed at "${seed.failedStepName}"${seed.failedStepError ? `: ${truncate(oneLine(seed.failedStepError), 80)}` : ''}`
            : seed.errorMessage
              ? ` — ${truncate(oneLine(seed.errorMessage), 80)}`
              : ' — failed'
          : '';
      const ref = seed.seedRunId ? `  (seed run ${seed.seedRunId})` : '';
      lines.push(`- ${glyph} ${label}${detail}${ref}`);
    }
    if (seedRuns.some((s) => s.status === 'failed')) {
      lines.push(
        'A failed seed often IS the root cause — inspect the seed run before the steps below.'
      );
    }
    lines.push('');
  }

  // Step table.
  const sortedSteps = input.steps.slice().sort((a, b) => a.orderIndex - b.orderIndex);
  const counts = countByStatus(sortedSteps);
  const summaryLine = `${sortedSteps.length} total — ${formatCounts(counts)}`;
  lines.push(`## Steps  (${summaryLine})`);
  const renderedSteps = sortedSteps.map((s) => formatStepRow(s, baselineMs));
  // Cap to first 30 + last failed/error steps if a run is huge — but normally
  // every step gets a row. Test pins ≤80 total lines so giant runs degrade gracefully.
  const visibleSteps =
    renderedSteps.length > 30
      ? [
          ...renderedSteps.slice(0, 25),
          `   …  (${renderedSteps.length - 30} more steps elided — see ledger.md)`,
          ...renderedSteps.slice(-5),
        ]
      : renderedSteps;
  for (const row of visibleSteps) lines.push(row);
  lines.push('');

  // Errors observed (capped).
  const networkErrors = input.network.filter(
    (n) => typeof n.status === 'number' && n.status >= 400
  );
  const consoleErrors = input.consoleLogs.filter((c) => c.type === 'error' || c.type === 'warning');
  if (networkErrors.length > 0 || consoleErrors.length > 0) {
    lines.push('## Errors observed');
    const ne = networkErrors.length;
    const ce = consoleErrors.length;
    if (ne > 0) {
      const sample = networkErrors[0]!;
      lines.push(
        `- ${ne}× network ${sample.status ?? '?'}: ${sample.method} ${sample.url}${
          sample.timestampMs !== null
            ? `   ${formatRelativePrefix(sample.timestampMs, baselineMs)}`
            : ''
        }${ne > 1 ? ` (+${ne - 1} more — see logs/network-errors.jsonl)` : ''}`
      );
    }
    if (ce > 0) {
      const sample = consoleErrors[0]!;
      lines.push(
        `- ${ce}× console.${sample.type}: ${truncate(oneLine(sample.text), 100)}${
          sample.timestampMs !== null
            ? `   ${formatRelativePrefix(sample.timestampMs, baselineMs)}`
            : ''
        }${ce > 1 ? ` (+${ce - 1} more — see logs/console-errors.jsonl)` : ''}`
      );
    }
    lines.push('');
  }

  // Notable events: browser crashes, cross-host navigations, cache misses on the failed step.
  const notables: string[] = [];
  for (const c of (input.crashes ?? []).slice(0, MAX_NOTABLE_EVENTS)) {
    notables.push(
      `- browser crash auto-recovered: ${c.label}   [T+${formatRelative(c.relativeTimestampMs)}]`
    );
  }
  const crossHost = input.navigation.filter((n) => n.crossHost);
  for (const n of crossHost.slice(0, MAX_NOTABLE_EVENTS)) {
    notables.push(
      `- cross-host nav: ${n.from ?? '?'} → ${n.to ?? '?'}${
        n.timestampMs !== null ? `   ${formatRelativePrefix(n.timestampMs, baselineMs)}` : ''
      }`
    );
  }
  const failedStep = sortedSteps.find((s) => s.status === 'failed');
  if (failedStep && failedStep.cacheOutcome) {
    notables.push(
      `- failed step cache outcome: ${failedStep.cacheOutcome}${failedStep.cacheHitSource ? `/${failedStep.cacheHitSource}` : ''}`
    );
  }
  if (notables.length > 0) {
    lines.push('## Notable events');
    for (const n of notables) lines.push(n);
    lines.push('');
  }

  // History hint.
  const historyHint = buildHistoryHint(
    input.recentRuns,
    input.runId,
    input.lastGreenComparison ?? null,
    input.greenRunAccess ?? 'cli',
    input.cliCommand ?? 'canary'
  );
  if (historyHint.length > 0) {
    lines.push('## History');
    for (const h of historyHint) lines.push(h);
    lines.push('');
  }

  // Drill-down pointers.
  lines.push('## Drill-down');
  const failedRollup =
    input.reportedFailure.failedStepId && failedStep
      ? stepRollupRelativePath(failedStep.orderIndex, failedStep.id)
      : null;
  if (failedRollup) {
    lines.push(`- Failed step rollup:  \`${failedRollup}\``);
  }
  lines.push(
    `- Per-step rollups:    \`${WORKSPACE_PATHS.steps}/*.md\` (ls steps/ for the full list)`
  );
  lines.push(
    `- Full ledger TOC:     \`${WORKSPACE_PATHS.ledgerIndex}\` (read before windowing into ledger.md)`
  );
  lines.push(
    `- Screenshots:         \`${WORKSPACE_PATHS.browserFrames}/<NNNN>-...png\` (read with vision)`
  );
  lines.push(`- Workflow nodes:      \`${WORKSPACE_PATHS.workflowNodes}/<nodeId>.json\``);
  lines.push('');

  await fs.writeFile(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.summary),
    lines.join('\n'),
    'utf-8'
  );
};

// ============================================================================
// Helpers
// ============================================================================

const STATUS_GLYPH: Record<string, string> = {
  success: '✓',
  passed: '✓',
  failed: '✗',
  skipped: '-',
  pending: '·',
  running: '⋯',
  waiting: '⋯',
  canceled: '⊘',
};

const formatStepRow = (s: SummaryStep, baselineMs: number | null): string => {
  const glyph = STATUS_GLYPH[s.status] ?? '?';
  const ord = String(s.orderIndex).padStart(3, ' ');
  const tplus =
    s.startedAtMs !== null && baselineMs !== null
      ? formatRelative(s.startedAtMs - baselineMs).padStart(11, ' ')
      : '          ?';
  const name = s.name ?? '(unnamed)';
  const namePadded = truncate(name, 35).padEnd(35, ' ');
  const dur =
    s.status === 'failed'
      ? `FAILED${s.errorMessage ? ` — ${truncate(oneLine(s.errorMessage), 40)}` : ''}`
      : s.status === 'skipped'
        ? 'skipped'
        : s.durationMs !== null
          ? `${s.durationMs}ms`
          : '?';
  const cache = s.cacheOutcome
    ? `   cache: ${s.cacheOutcome}${s.cacheHitSource ? `/${s.cacheHitSource}` : ''}`
    : '';
  return ` ${ord}  ${glyph}  ${tplus}  ${namePadded}  ${dur}${cache}`;
};

const countByStatus = (steps: ReadonlyArray<SummaryStep>): Map<string, number> => {
  const m = new Map<string, number>();
  for (const s of steps) m.set(s.status, (m.get(s.status) ?? 0) + 1);
  return m;
};

const formatCounts = (counts: Map<string, number>): string => {
  const order = [
    'success',
    'passed',
    'failed',
    'skipped',
    'pending',
    'running',
    'waiting',
    'canceled',
  ];
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const key of order) {
    if (counts.has(key)) {
      const count = counts.get(key)!;
      const emphasis =
        key === 'failed' && count > 0 ? `**${count} ${key.toUpperCase()}**` : `${count} ${key}`;
      parts.push(emphasis);
      seen.add(key);
    }
  }
  for (const [k, v] of counts) {
    if (!seen.has(k)) parts.push(`${v} ${k}`);
  }
  return parts.join(', ');
};

const buildHistoryHint = (
  runs: ReadonlyArray<SummaryRecentRun>,
  currentRunId: string,
  lastGreenComparison: SummaryLastGreenComparison | null,
  greenRunAccess: GreenRunAccess,
  cliCommand: string
): string[] => {
  const lookback = runs.slice(0, MAX_HISTORY_LOOKBACK);
  if (lookback.length <= 1) return [];
  // Exclude the current run from the comparison if it's in the list.
  const others = lookback.filter((r) => r.workflowRunId !== currentRunId);
  if (others.length === 0) return [];
  const passed = others.filter((r) => isPassStatus(r.status)).length;
  const failed = others.filter((r) => isFailStatus(r.status)).length;
  const lastGreen = others.find((r) => isPassStatus(r.status));
  const regime = classifyHistoryRegime(others);
  const out: string[] = [];
  out.push(`${passed} of last ${others.length} ran passed; ${failed} failed.  Regime: ${regime}.`);
  if (lastGreen) {
    out.push(
      `Most recent green: testRunId=${lastGreen.testRunId}${
        lastGreen.startedAt ? ` (${lastGreen.startedAt.slice(0, 10)})` : ''
      }`
    );
    // Comparison-leaf workspaces report stats only — no instruction, no steer.
    if (greenRunAccess === 'none') return out;
    // Single source of truth for the printed id: prefer the data layer's
    // lastGreenComparison.greenRunId — the SAME id the orchestrator's comparison
    // gate keys on — so the agent is never steered to an id the gate (and
    // findRunById) won't accept. The recentRuns-derived fallback only applies
    // when the comparison metadata is absent entirely.
    const greenRunId =
      lastGreenComparison?.greenRunId ?? lastGreen.workflowRunId ?? lastGreen.testRunId;
    out.push(
      greenRunAccess === 'agent-tool'
        ? `Comparison is REQUIRED — call the materialize_run tool with runId=${greenRunId}, then read the returned workspace under history/runs/${greenRunId}/.`
        : `Comparison is REQUIRED — materialize via: ${cliCommand} workflow debug materialize ${greenRunId} --out history/last-green/`
    );
  }

  // Regime-gated steer. The regime narrows the hypothesis space but never forces a
  // verdict: an unchanged authored test eliminates only test_underspecified — a legit
  // app change, an app regression, an agent error, or a platform error all remain live
  // until the comparison discriminates between them.
  const authoredUnchanged = lastGreenComparison?.authoredUnchanged ?? null;
  const greenDate = lastGreen?.startedAt ? ` (${lastGreen.startedAt.slice(0, 10)})` : '';
  if (regime === 'STABLE_REGRESSION' && lastGreen) {
    if (authoredUnchanged === true) {
      out.push(
        `AUTHORED TEST UNCHANGED since last green${greenDate} — the authored workflow is byte-identical across this pass→fail boundary, so test_underspecified is off the table. That leaves FOUR live hypotheses: a legitimate app change (app_changed), an app regression (software_bug), an agent error (agent_confusion — e.g. verification/interpretation drift), or a platform error (platform_error — cache/replay/sandbox machinery). Do NOT default to app_changed. Follow the green→red methodology: establish HOW each side of the boundary executed (cache replay vs live agent), then compare. Only classify app_changed after confirming a concrete behavior delta against the materialized green run, and note in openQuestions that intent could not be verified from the sandbox.`
      );
    } else if (authoredUnchanged === false) {
      out.push(
        `AUTHORED TEST CHANGED since last green${greenDate} — the test definition was edited. Compare to judge whether the new instructions are ambiguous (test_underspecified) before blaming the app.`
      );
    } else {
      out.push(
        `Stably green before this break — comparison REQUIRED. The pass→fail boundary alone does not discriminate between app_changed, software_bug, agent_confusion, and platform_error; materialize the green run and follow the green→red methodology before classifying.`
      );
    }
  } else if (regime === 'FLAKY') {
    out.push(
      `This test is FLAKY (pass/fail oscillated over the window) — a single green→red does NOT imply an app change. Weigh agent_confusion / intermittent dependency_issue / data_collision. Comparison may still help, but do not force an app_changed verdict without a clear, persistent behavior delta.`
    );
  }
  return out;
};

const formatRelativePrefix = (timestampMs: number, baselineMs: number | null): string =>
  baselineMs !== null ? `[${formatRelative(timestampMs - baselineMs)}]` : '[T+?]';

// ============================================================================
// Candidate upstream divergences (heuristic hints)
// ============================================================================

interface DivergenceCandidate {
  step: SummaryStep;
  /** One short clause per fired heuristic. */
  reasons: string[];
  /** Configured input values (heuristic C enrichment) — annotation only. */
  intendedInputs: SummaryStepVariable[];
  /** True when a higher-precision heuristic (B churn or D lineage) fired, vs A-only. */
  highSignal: boolean;
}

/**
 * Compute non-failed upstream steps worth investigating first. Domain-agnostic,
 * structural/statistical signals only — never app-specific. A step is a candidate when
 * ANY heuristic fires:
 *
 *   A. it ejected from cache replay to the live agent (`cacheOutcome === 'fallback'`),
 *      so its post-state was never cache-verified;
 *   B. its agent churn is a high outlier (many thoughts + repeated DOM probes) yet it
 *      reported success — a confused-but-green step;
 *   D. it produced (via an extractor) a `$var.X` the failing step depends on — confirm
 *      that value is valid before reading the current page state as external behavior.
 *
 * C is enrichment, not a trigger: flagged steps get their configured input values
 * appended so the agent can compare intent against the live UI.
 */
const computeCandidateUpstreamDivergences = (input: SummaryInput): DivergenceCandidate[] => {
  // Failure investigations only — a clean pass has no "failure" to walk back from.
  if (isCleanPassStatus(input.runStatus)) return [];
  const steps = input.steps;
  if (steps.length === 0) return [];

  // Failure anchor: only steps strictly before it are "upstream". Null when the failure
  // is run-level with no failed-step index — then every non-failed step is eligible.
  const anchor = input.reportedFailure.failedStepOrderIndex;
  const referencedVars = new Set(input.failedStepReferencedVars ?? []);

  // Churn outlier threshold (heuristic B): scale to the run's distribution but floor at
  // an absolute minimum. Median is taken over ALL steps (zero-churn steps included) so a
  // lone churny step still reads as an outlier against its quiet neighbors; in a uniformly
  // churny run the median rises and suppresses the noise. The CHURN_MIN_THOUGHTS floor is
  // the effective bar in the common case where most steps have little churn.
  const allCounts = steps.map((s) => s.thoughtCount ?? 0).sort((a, b) => a - b);
  const median = allCounts.length === 0 ? 0 : allCounts[Math.floor((allCounts.length - 1) / 2)]!;
  const churnThreshold = Math.max(CHURN_MIN_THOUGHTS, median * CHURN_MEDIAN_MULTIPLE);
  const maxThoughts = allCounts.length > 0 ? allCounts[allCounts.length - 1]! : 0;

  const candidates: DivergenceCandidate[] = [];
  for (const s of steps) {
    if (s.status === 'failed') continue;
    if (anchor !== null && s.orderIndex >= anchor) continue;

    const reasons: string[] = [];
    let highSignal = false;

    // A — cache eject on a non-failed step. Lower precision: in a run where an app change
    // broke many recipes, most steps eject, so A alone is ranked below B/D below.
    if (s.cacheOutcome === 'fallback') {
      const why = s.cacheFailureCategory ?? s.cacheOutcomeReason;
      reasons.push(`ejected cache→live${why ? ` (${why})` : ''}; post-state never cache-verified`);
    }

    // B — agent-churn outlier yet marked success.
    const thoughts = s.thoughtCount ?? 0;
    const probes = s.domProbeCount ?? 0;
    if (thoughts >= churnThreshold && probes >= CHURN_MIN_DOM_PROBES) {
      const lead = thoughts === maxThoughts ? 'highest churn in run' : 'high agent churn';
      reasons.push(`${lead} (${thoughts} thoughts, ${probes} DOM probes) yet marked ${s.status}`);
      highSignal = true;
    }

    // D — self-created-artifact lineage: this step extracted a $var the failing step uses.
    for (const v of s.variablesSet ?? []) {
      if (v.extracted && referencedVars.has(v.key)) {
        reasons.push(
          `produced $var.${v.key} (extracted), which the failing step depends on — confirm that ` +
            'value is valid before treating the current page state as external behavior'
        );
        highSignal = true;
      }
    }

    if (reasons.length === 0) continue;

    // C — enrichment: configured inputs on the flagged step (annotation, not a trigger).
    const intendedInputs = (s.variablesSet ?? [])
      .filter((v) => v.source === 'inputValue' && v.value !== null)
      .slice(0, MAX_INTENDED_INPUTS);

    candidates.push({ step: s, reasons, intendedInputs, highSignal });
  }

  // Rank high-signal (B/D) candidates ahead of A-only before the cap, so a run where many
  // steps merely ejected cache can't crowd the higher-precision churn/lineage hints out of
  // the list. Then restore order-index order so the displayed block reads chronologically.
  candidates.sort((a, b) =>
    a.highSignal !== b.highSignal ? (a.highSignal ? -1 : 1) : a.step.orderIndex - b.step.orderIndex
  );
  const selected = candidates.slice(0, MAX_DIVERGENCE_CANDIDATES);
  selected.sort((a, b) => a.step.orderIndex - b.step.orderIndex);
  return selected;
};

/** Render the "⚠ Candidate upstream divergences" section, or [] when there are none. */
const buildDivergenceSection = (input: SummaryInput): string[] => {
  const candidates = computeCandidateUpstreamDivergences(input);
  if (candidates.length === 0) return [];
  const out: string[] = [];
  out.push('## ⚠ Candidate upstream divergences');
  out.push(
    'Heuristic candidates only — each is a place to START, not a root cause. Confirm or rule out ' +
      "every one by walking backwards and verifying the step's EFFECT (the value selected, the field " +
      'populated, the record created) from its screenshot/DOM — not its success flag. This list is NOT ' +
      'exhaustive; an upstream cause can exist with no hint here.'
  );
  for (const c of candidates) {
    const name = c.step.name ?? '(unnamed)';
    const reasons = c.reasons.join('; ');
    const inputs =
      c.intendedInputs.length > 0
        ? `  [configured inputs: ${c.intendedInputs
            .map((v) => `${v.key}=${truncate(oneLine(String(v.value)), 40)}`)
            .join(', ')}]`
        : '';
    const ref = `  \`${stepRollupRelativePath(c.step.orderIndex, c.step.id)}\``;
    out.push(`- Step ${c.step.orderIndex} "${name}": ${reasons}.${inputs}${ref}`);
  }
  out.push('');
  return out;
};
